import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { loadNewSettlement, createSettlementFn } from "@/lib/server-fns/settlements";
import {
  Container,
  Heading,
  Button,
  Input,
  HStack,
  Card,
  VStack,
  Stack,
  NativeSelect,
  NumberInput,
  Field,
  SegmentGroup,
  For,
} from "@chakra-ui/react";
import { showErrorToast } from "@/lib/toast-utils";
import { buildParticipantOptions } from "@/lib/user-utils";
import { CURRENCY_ICONS, SUPPORTED_CURRENCIES } from "@/lib/constants";
import { LuArrowLeft } from "react-icons/lu";
import { useState } from "react";
import { InputGroup } from "@/components/ui/input-group";

export const Route = createFileRoute("/_authenticated/settlements/new")({
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    fromUserId?: string;
    toUserId?: string;
    amount?: string;
    currency?: string;
  } => ({
    fromUserId: (search.fromUserId as string) || undefined,
    toUserId: (search.toUserId as string) || undefined,
    amount: (search.amount as string) || undefined,
    currency: (search.currency as string) || undefined,
  }),
  loader: () => loadNewSettlement(),
  component: SettlementNew,
});

function SettlementNew() {
  const { friends, currentUser, defaultCurrency: userDefaultCurrency } = Route.useLoaderData();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const [date, setDate] = useState<string>(new Date().toISOString().split("T")[0]);
  const defaultCurrency = search.currency || userDefaultCurrency;
  const [currency, setCurrency] = useState(defaultCurrency);
  const defaultAmount = search.amount?.replace(/"/g, "") || "";

  const userOptions = buildParticipantOptions({ currentUser, friends });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    try {
      const isoDate = new Date(date).toISOString().split("T")[0];

      await createSettlementFn({
        data: {
          fromUserId: formData.get("fromUserId") as string,
          toUserId: formData.get("toUserId") as string,
          amount: formData.get("amount") as string,
          currency: formData.get("currency") as string,
          date: isoDate,
        },
      });

      navigate({ to: "/expenses" });
    } catch (error) {
      showErrorToast("Failed to record settlement", error);
    }
  };

  return (
    <Container maxW="md" px={{ base: "3", md: "4" }}>
      <HStack mb="6">
        <Button asChild variant="ghost">
          <Link to="/expenses">
            <LuArrowLeft size={16} />
            Back to Activity
          </Link>
        </Button>
      </HStack>

      <Card.Root variant="outline">
        <Card.Body p={{ base: "4", md: "8" }}>
          <Heading as="h2" size="lg" mb="6">
            Record Settlement
          </Heading>

          <form onSubmit={handleSubmit}>
            <VStack gap="4" align="stretch">
              <Field.Root required>
                <Field.Label>Who paid?</Field.Label>
                <NativeSelect.Root width="full">
                  <NativeSelect.Field
                    name="fromUserId"
                    defaultValue={search.fromUserId || currentUser.id}
                  >
                    {userOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
              </Field.Root>

              <Field.Root required>
                <Field.Label>Paid to</Field.Label>
                <NativeSelect.Root width="full">
                  <NativeSelect.Field name="toUserId" defaultValue={search.toUserId || ""}>
                    <option value="" disabled>
                      Select who received the payment
                    </option>
                    {userOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
              </Field.Root>

              <Field.Root required>
                <Field.Label>Currency</Field.Label>
                <SegmentGroup.Root
                  name="currency"
                  defaultValue={defaultCurrency}
                  onValueChange={(details) => setCurrency(details.value ?? defaultCurrency)}
                  width="full"
                  css={{
                    "--segment-indicator-bg": "colors.loonie.solid",
                    "--segment-indicator-shadow": "shadows.md",
                  }}
                >
                  <SegmentGroup.Indicator />
                  <For each={SUPPORTED_CURRENCIES}>
                    {(code) => (
                      <SegmentGroup.Item key={code} value={code}>
                        <SegmentGroup.ItemText>
                          <HStack gap="1.5">
                            {CURRENCY_ICONS[code]}
                            {code}
                          </HStack>
                        </SegmentGroup.ItemText>
                        <SegmentGroup.ItemHiddenInput />
                      </SegmentGroup.Item>
                    )}
                  </For>
                </SegmentGroup.Root>
              </Field.Root>

              <Field.Root required>
                <Field.Label>Amount</Field.Label>
                <Field.HelperText>How much was paid to settle up</Field.HelperText>
                <NumberInput.Root
                  name="amount"
                  min={0.01}
                  step={0.01}
                  defaultValue={defaultAmount || undefined}
                  width="full"
                >
                  <NumberInput.Control />
                  <InputGroup startElement={CURRENCY_ICONS[currency]}>
                    <NumberInput.Input placeholder="50.00" />
                  </InputGroup>
                </NumberInput.Root>
              </Field.Root>

              <Field.Root required>
                <Field.Label>Date</Field.Label>
                <Input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  width="full"
                />
              </Field.Root>

              <Stack
                direction={{ base: "column-reverse", sm: "row" }}
                justify="flex-end"
                mt="6"
                gap="3"
                width="full"
              >
                <Button asChild variant="ghost" width={{ base: "full", sm: "auto" }}>
                  <Link to="/expenses">Cancel</Link>
                </Button>
                <Button type="submit" width={{ base: "full", sm: "auto" }}>
                  Record Settlement
                </Button>
              </Stack>
            </VStack>
          </form>
        </Card.Body>
      </Card.Root>
    </Container>
  );
}
